/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, mergeMap, skip, take } from 'rxjs/operators';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons/faBookOpen';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { DiaryInstanceInterface } from '../../../models/interface/diary-instance.interface';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { BuddyInstanceInterface } from '../../../models/interface/buddy/buddy-instance.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { GroupSharedService } from '../../../services/shared/group-shared/group-shared.service';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { Store } from '@ngrx/store';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { BuddyStore } from '../../../store/buddy/component-store/buddy.store';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { InterventionStore } from '../../../store/intervention/component-store/intervention.store';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { BuddyLastActivityInterface } from '../../../models/interface/buddy/buddy-last-activity.interface';
import { ActivityInterface } from '../../../models/interface/activity.interface';
import { PaginationData, PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

@Component({
  selector: 'app-intervention-buddy',
  templateUrl: './group-buddy.component.html',
  styleUrls: ['./group-buddy.component.scss'],
  providers: [BuddyStore, DiaryStore, InterventionStore, InterventionInstanceStore, StudyStore],
})
export class GroupBuddyComponent implements OnDestroy, OnInit {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faBookOpen = faBookOpen;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faPen = faPen;

  public studyId: number;
  public isLoading$: Observable<boolean>;

  public diaryInstances: DiaryInstanceInterface[] = [];
  public buddyInstances: BuddyInstanceInterface[] = [];

  public inactiveBuddyLastActivities: BuddyLastActivityInterface[] = [];
  public activeBuddyLastActivities: BuddyLastActivityInterface[] = [];

  public instances: InterventionInstanceInterface[] = [];
  public runningInstances: InterventionInstanceInterface[] = [];

  public allBuddyActivitiesOfStudy: ActivityInterface[] = [];

  // Filter
  public filter = { searchFilter: '' };

  public days = 21;

  public param = { intervention_name: '...', days: this.days };

  // Data provided by StudyService
  public studyMembers: UserInterface[];
  public diaries: DiaryInterface[] = [];
  public studyCollaborators: UserInterface[] = [];

  public interventionsBuddy: InterventionInterface[] = [];
  public profile$: Observable<ProfileInterface>;
  public profile: ProfileInterface;

  public buddiesData$: Observable<PaginationData<BuddyInstanceInterface>>;
  public buddies$: Observable<BuddyInstanceInterface[]>;
  public pagedBuddies$: Observable<BuddyInstanceInterface[]>;
  public isECoach$: Observable<boolean>;
  public isManager$: Observable<boolean>;
  public isCollapse$: Observable<boolean>;

  public buddiesOfStudy$: Observable<BuddyInstanceInterface[]>;

  public diaryInstances$: Observable<DiaryInstanceInterface[]>;

  public diariesOfStudy$: Observable<DiaryInterface[]>;

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  private allInterventionsOfStudy$: Observable<InterventionInterface[]>;

  private allMyInstancesOfStudy$: Observable<InterventionInstanceInterface[]>;

  private allInactiveBuddyLastActivities$: Observable<BuddyLastActivityInterface[]>;

  private allActiveBuddyLastActivities$: Observable<BuddyLastActivityInterface[]>;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;

  private studyMembers$: Observable<UserInterface[]>;

  private getCSVBuddyByLastActivityResponse$: Observable<any>;

  private buddiesDataSubject = new BehaviorSubject<PaginationData<BuddyInstanceInterface>>({ items: [] });
  private buddiesSubject = new BehaviorSubject<BuddyInstanceInterface[]>([]);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private searchTextSubject = new BehaviorSubject<string>('');

  private pagedBuddiesSubject = new BehaviorSubject<BuddyInstanceInterface[]>([]);

  private allBuddyActivitiesOfStudy$: Observable<ActivityInterface[]>;

  private isECoachSubject = new BehaviorSubject<boolean>(false);
  private isManagerSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: GroupSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private buddyStore: BuddyStore,
    private diaryStore: DiaryStore,
    private interventionStore: InterventionStore,
    private interventionInstanceStore: InterventionInstanceStore,
    private studyStore: StudyStore,
    private store: Store<{
      myProfile: ProfileInterface;
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
    }>,
  ) {
    this.profile$ = store.select('myProfile');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.buddiesData$ = this.buddiesDataSubject.asObservable();
    this.buddies$ = this.buddiesSubject.asObservable();
    this.pagedBuddies$ = this.pagedBuddiesSubject.asObservable();
    this.isECoach$ = this.isECoachSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();

    this.buddiesOfStudy$ = this.buddyStore.allBuddiesOfStudy$;
    this.allInactiveBuddyLastActivities$ = this.buddyStore.allInactiveBuddyLastActivities$;
    this.allActiveBuddyLastActivities$ = this.buddyStore.allActiveBuddyLastActivities$;
    this.getCSVBuddyByLastActivityResponse$ = this.buddyStore.getCSVBuddyByLastActivityResponse$;
    this.allBuddyActivitiesOfStudy$ = this.buddyStore.allBuddyActivitiesOfStudy$;
    this.diaryInstances$ = this.diaryStore.allDiaryInstances$;
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.allInterventionsOfStudy$ = this.interventionStore.allInterventionsOfSpecificStudy$;
    this.allMyInstancesOfStudy$ = this.interventionInstanceStore.allMyInstancesOfStudy$;
    this.studyMembers$ = this.studyStore.studyMembers$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.profile$
        .pipe(filter((user) => user !== null))
        .subscribe((result) => (this.profile = result)),
    );

    this.subscriptions.push(
      this.sharedService.isECoach$.subscribe((value) => {
        this.isECoachSubject.next(value);
      })
    );

    this.subscriptions.push(
      this.allBuddyActivitiesOfStudy$.subscribe((activities) => {
        this.allBuddyActivitiesOfStudy = activities;
      })
    );

    this.subscriptions.push(
      this.allInactiveBuddyLastActivities$.subscribe((activities) => {
        this.inactiveBuddyLastActivities = activities;
      })
    );

    this.subscriptions.push(
      this.allActiveBuddyLastActivities$.subscribe((activities) => {
        this.activeBuddyLastActivities = activities;
      })
    );

    this.applyBuddyFilter();
  }

  public applyBuddyFilter(setFirst?: boolean): void {
    this.reloadLastActivities();
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadBuddyList(setFirst).subscribe(() => {
        this.isLoadingSubject.next(false);
      })
    );
  }

  public reloadBuddyList(setFirst?: boolean): Observable<any> {
    const pageIndex = this.paginator?.paginator ? this.paginator.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator.paginator.pageSize : 20;

    return this.sharedService.isManager$.pipe(
      mergeMap((value: boolean) => {
        this.isManagerSubject.next(value);
        return this.sharedService.sourceGroup$;
      }),
      filter((value: StudyInterface) => !!value),
      mergeMap((value: StudyInterface) => {
        this.studyId = value.id;
        this.param = { intervention_name: value.attributes.name, days: this.days };

        const isManager = this.isManagerSubject.value;
        // Get buddy update activities of study
        if (isManager) {
          this.buddyStore.getAllBuddyActivitiesStudyEM(this.studyId);
        } else {
          this.buddyStore.getAllBuddyActivitiesStudy(this.studyId);
        }

        this.interventionStore.getAllInterventionsOfSpecificStudy({ studyId: this.studyId, isActive: 1 });
        const interventions = this.allInterventionsOfStudy$.pipe(skip(1), take(1));

        if (isManager) {
          this.interventionInstanceStore.getAllInstancesOfStudy({ studyId: this.studyId });
        } else {
          this.interventionInstanceStore.getMyInstancesOfStudy({ studyId: this.studyId });
        }
        const interventionInstances = this.allMyInstancesOfStudy$.pipe(skip(1), take(1));

        this.diaryStore.getDiariesOfStudy({ studyId: this.studyId });
        const diaries = this.diariesOfStudy$.pipe(skip(1), take(1));

        this.store.dispatch({
          type: StudyActionTypes.getCollaboratorsType,
          payload: { studyId: this.studyId, include: 'roles' },
        });
        this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.studyId));
        const collaborators = this.collaboratorsOfStudy$.pipe(skip(1), take(1));

        if (isManager) {
          this.diaryStore.getAllDiaryInstancesOfStudy({ studyId: this.studyId });
        } else {
          this.diaryStore.getAllInstancesOfDiaryECoach({ studyId: this.studyId });
        }
        const diaryInstances = this.diaryInstances$.pipe(skip(1), take(1));

        if (isManager) {
          this.studyStore.getMembers({ studyId: this.studyId });
        } else {
          this.studyStore.getECoachMembers({ studyId: this.studyId });
        }
        const studyMembers = this.studyMembers$.pipe(skip(1), take(1));

        if (isManager) {
          this.buddyStore.getAllBuddiesOfStudyEM(this.studyId);
        } else {
          this.buddyStore.getAllBuddiesOfStudy(this.studyId);
        }
        const buddyInstances = this.buddiesOfStudy$.pipe(skip(1), take(1));
        return forkJoin({
          interventions,
          interventionInstances,
          diaries,
          collaborators,
          diaryInstances,
          studyMembers,
          buddyInstances,
        });
      }),
      mergeMap((result) => {
        this.interventionsBuddy = result.interventions.filter((intervention) => intervention.attributes?.buddy_support === 1);
        this.instances = result.interventionInstances;
        this.runningInstances = this.instances
          .filter(
            (instance) => (
              !instance.attributes.progress.current_state.match(/^(canceled|completed)$/) &&
              this.interventionsBuddy.some(
                (intervention) => intervention.id.toString() === instance.attributes.intervention_id.toString(),
              )
            ),
          );
        this.diaries = result.diaries;
        this.studyCollaborators = result.collaborators.collaborators;
        this.diaryInstances = result.diaryInstances.filter(
          (diaryInstance) =>
            !!diaryInstance.attributes.patient_id
            && this.diaries.some((diary) => diary.id.toString() === diaryInstance.attributes.diary_id.toString())
            && this.instances.some((instance) => instance.id.toString() === diaryInstance.attributes.intervention_instance_id.toString()),
        );
        this.studyMembers = result.studyMembers;
        this.buddyInstances = result.buddyInstances;
        this.buddiesSubject.next(this.buddyInstances);
        return this.performFilterAndPagination(setFirst, pageIndex, pageSize);
      }),
    );
  }

  public updatePagedBuddies(event: BuddyInstanceInterface[]) {
    if (event) {
      this.pagedBuddiesSubject.next(event);
    }
  }

  public reloadLastActivities(): void {
    this.param = { intervention_name: '...', days: this.days };
    this.subscriptions.push(
      this.sharedService.sourceGroup$.pipe(filter((value: StudyInterface) => !!value)).subscribe((value: StudyInterface) => {
        this.buddyStore.getBuddyLastActivities({ studyId: value.id, days: this.days });
      })
    );
  }

  public openDialogBuddyInstanceDetails(buddyInstance: BuddyInstanceInterface): void {
    this.helperDialog
      .openDialogBuddyInstanceDetails(
        buddyInstance,
        this.studyMembers,
        this.studyCollaborators,
        this.interventionsBuddy,
        this.runningInstances,
        this.diaryInstances,
        this.diaries,
        this.isManagerSubject.value,
        this.studyId,
      )
      .afterClosed()
      .pipe(debounceTime(2000))
      .subscribe(() => {});
  }

  private getNonBuddyStudyMembers(
    diaryInstances: DiaryInstanceInterface[],
    buddyInstance?: BuddyInstanceInterface,
  ): UserInterface[] {
    return this.studyMembers.filter((user) => {
      const userId = user.id.toString();
      const userHasNoDiaryInstance =
        diaryInstances.every((instance) => instance.attributes.patient_id.toString() !== userId);
      if (userHasNoDiaryInstance) {
        return false;
      }
      for (const i in this.buddyInstances) {
        const buddy = this.buddyInstances[i];
        if (buddyInstance === undefined || buddy.id.toString() !== buddyInstance.id.toString()) {
          const buddyIDs = buddy.attributes?.buddies_ids;
          for (const b in buddyIDs) {
            if (user.id === buddyIDs[b]) {
              return false;
            }
          }
        }
      }
      return true;
    });
  }

  public openDialogBuddyInstanceCreate(): void {
    const isManager = this.isManagerSubject.value;
    let myInstances: InterventionInstanceInterface[];
    let myDiaryInstances: DiaryInstanceInterface[];
    if (isManager) {
      myInstances = this.runningInstances;
      myDiaryInstances = this.diaryInstances;
    } else {
      const profileId = this.profile.id.toString();
      myInstances = this.runningInstances.filter(
        (instance) => instance.attributes.ecoach_id?.some(
          (id) => id.toString() === profileId,
        ),
      );
      myDiaryInstances = this.diaryInstances.filter(
        (diaryInstance) => myInstances.some(
          (instance) => instance.id == diaryInstance.attributes.intervention_instance_id,
        ),
      );
    }
    const membersWithoutBuddies = this.getNonBuddyStudyMembers(myDiaryInstances);
    this.helperDialog
      .openDialogBuddyInstanceCreate(
        membersWithoutBuddies,
        this.interventionsBuddy,
        myInstances,
        myDiaryInstances,
        this.diaries,
        this.studyId,
        isManager,
      )
      .afterClosed()
      .pipe(mergeMap(
        () => {
          this.isLoadingSubject.next(true);
          return this.reloadBuddyList();
        },
      ))
      .subscribe(() => this.isLoadingSubject.next(false));
  }

  public openDialogBuddyInstanceDelete(buddyInstance: BuddyInstanceInterface): void {
    this.helperDialog
      .openDialogBuddyInstanceDelete(
        buddyInstance,
        this.studyMembers,
        this.studyCollaborators,
        this.studyId,
        this.isManagerSubject.value,
      )
      .afterClosed()
      .pipe(mergeMap(
        () => {
          this.isLoadingSubject.next(true);
          return this.reloadBuddyList();
        },
      ))
      .subscribe(() => this.isLoadingSubject.next(false));
  }

  public openDialogBuddyInstanceUpdate(buddyInstance: BuddyInstanceInterface): void {
    const isManager = this.isManagerSubject.value;
    let myInstances: InterventionInstanceInterface[];
    let myDiaryInstances: DiaryInstanceInterface[];
    if (isManager) {
      myInstances = this.runningInstances;
      myDiaryInstances = this.diaryInstances;
    } else {
      const profileId = this.profile.id.toString();
      myInstances = this.runningInstances.filter(
        (instance) => instance.attributes.ecoach_id?.some(
          (id) => id.toString() === profileId
        ),
      );
      myDiaryInstances = this.diaryInstances.filter(
        (diaryInstance) => myInstances.some(
          (instance) => instance.id == diaryInstance.attributes.intervention_instance_id,
        ),
      );
    }
    const membersWithoutBuddies = this.getNonBuddyStudyMembers(myDiaryInstances, buddyInstance);
    this.helperDialog
      .openDialogBuddyInstanceUpdate(
        buddyInstance,
        membersWithoutBuddies,
        this.interventionsBuddy,
        myInstances,
        myDiaryInstances,
        this.diaries,
        this.studyCollaborators,
        this.studyId,
        isManager,
      )
      .afterClosed()
      .pipe(mergeMap(
        () => {
          this.isLoadingSubject.next(true);
          return this.reloadBuddyList();
        },
      ))
      .subscribe(() => this.isLoadingSubject.next(false));
  }

  public openDialogBuddyInstanceSwap(): void {
    this.helperDialog
      .openDialogBuddyInstanceSwap(
        this.buddyInstances,
        this.studyMembers,
        this.interventionsBuddy,
        this.runningInstances,
        this.diaryInstances,
        this.diaries,
        this.studyCollaborators,
        this.studyId,
      )
      .afterClosed()
      .pipe(mergeMap(
        () => {
          this.isLoadingSubject.next(true);
          return this.reloadBuddyList();
        },
      ))
      .subscribe(() => this.isLoadingSubject.next(false));
  }

  public openDialogInterventionInstanceDetails(buddy: BuddyInstanceInterface, userId: number): void {
    const interventionInstanceId: number = this.getInterventionInstanceIdOfBuddy(buddy, userId);
    if (interventionInstanceId) {
      this.helperDialog
        .openDialogInterventionInstanceDetails(interventionInstanceId, this.param)
        .afterClosed()
        .subscribe(() => {});
    }
  }

  public openDialogDiaryAnswersheetPreview(buddy: BuddyInstanceInterface, userId: number): void {
    const diaryInstanceId: number = this.getDiaryInstanceIdOfBuddy(buddy, userId);
    if (diaryInstanceId) {
      const foundDiaryInstance: DiaryInstanceInterface = this.diaryInstances.find(
        (diaryInstance: DiaryInstanceInterface) => diaryInstance.id.toString() === diaryInstanceId.toString()
      );
      if (foundDiaryInstance) {
        const foundDiary: DiaryInterface = this.diaries.find(
          (diary: DiaryInterface) => diary.id.toString() === foundDiaryInstance.attributes.diary_id.toString()
        );
        if (foundDiary) {
          this.helperDialog
            .openDialogDiaryAnswersheetPreview(diaryInstanceId, foundDiary, this.isManagerSubject.value)
            .afterClosed()
            .pipe(debounceTime(2000))
            .subscribe(() => {});
        }
      }
    }
  }

  public getInterventionInstanceIdOfBuddy(buddy: BuddyInstanceInterface, userId: number): number {
    const findInterventionInstance = buddy.attributes.intervention_instances_ids.find(
      (value) => value.user_id.toString() === userId.toString()
    );
    return findInterventionInstance ? findInterventionInstance.intervention_instance_id : null;
  }

  public getDiaryInstanceIdOfBuddy(buddy: BuddyInstanceInterface, userId: number): number {
    const findDiaryInstance = buddy.attributes.diary_instances_ids.find(
      (value) => value.user_id.toString() === userId.toString()
    );
    return findDiaryInstance ? findDiaryInstance.diary_instance_id : null;
  }

  public isGuidingECoach(userId: number, buddy: BuddyInstanceInterface): boolean {
    return buddy.attributes.ecoach_id.some((id: number) => id.toString() === userId.toString());
  }

  private getLastActivitiesOfBuddies(
    buddyActivities: BuddyLastActivityInterface[],
    buddyId: number,
    userId: number,
  ): BuddyLastActivityInterface | undefined {
    if (userId == null || buddyId == null) {
      return undefined;
    }
    let lastActivity: BuddyLastActivityInterface | undefined = undefined;
    for (const activity of buddyActivities) {
      if (
        (lastActivity === undefined || activity.id < lastActivity.id)
        && activity.attributes.buddy_id?.toString() === buddyId.toString()
        && activity.attributes.user_id?.toString() === userId.toString()
      ) {
        lastActivity = activity;
      }
    }
    return lastActivity;
  }

  public getLastActivitiesOfInactiveBuddiesSince(buddyId: number, userId: number): BuddyLastActivityInterface | undefined {
    return this.getLastActivitiesOfBuddies(this.inactiveBuddyLastActivities, buddyId, userId);
  }

  public getLastActivitiesOfActiveBuddiesSince(buddyId: number, userId: number): BuddyLastActivityInterface | undefined {
    return this.getLastActivitiesOfBuddies(this.activeBuddyLastActivities, buddyId, userId);
  }

  public generateCSVBuddyReport(): void {
    this.subscriptions.push(
      this.sharedService.sourceGroup$
        .pipe(
          filter((value: StudyInterface) => !!value),
          mergeMap((value: StudyInterface) => {
            this.buddyStore.getCSVBuddyByLastActivity({ studyId: value.id, isActive: false, days: this.days });
            return this.getCSVBuddyByLastActivityResponse$;
          })
        )
        .subscribe()
    );
  }

  public openDialogBuddyActivityReport(studyId?: number, buddyInstance?: BuddyInstanceInterface): void {
    this.helperDialog
      .openDialogBuddyActivityReport(studyId, this.studyMembers, buddyInstance)
      .afterClosed()
      .pipe(mergeMap(
        () => {
          this.isLoadingSubject.next(true);
          return this.reloadBuddyList();
        },
      ))
      .subscribe(() => this.isLoadingSubject.next(false));
  }

  public isValidDate(checkDate: any): boolean {
    return checkDate !== undefined && checkDate !== 'N/A';
  }

  public performFilterAndPagination(setFirst: boolean, pageIndex: number, pageSize: number): Observable<boolean> {
    return this.helper
      .searchBuddiesByInput(
        this.filter.searchFilter,
        this.searchTextSubject,
        this.buddyInstances,
        this.buddiesSubject,
        this.studyMembers,
        this.studyCollaborators,
        this.studyId,
      )
      .pipe(
        mergeMap(
          () => this.helper.setPagedContent(
            this.buddiesSubject,
            this.pagedBuddiesSubject,
            setFirst,
            pageIndex,
            pageSize,
            this.buddiesDataSubject,
          ),
        ),
      );
  }

  public getSwappingActivitiesOfBuddy(buddyId: number, userId: number): ActivityInterface[] {
    if (buddyId == null || userId == null) {
      return [];
    }
    const bId = buddyId.toString();
    const uId = userId.toString();
    return this.helper.getActivityByNewest(
      this.allBuddyActivitiesOfStudy.filter(
        (activity) =>
          activity.attributes?.entity_id?.toString() === uId &&
          activity.attributes.info?.buddy_id?.toString() === bId,
      ),
    );
  }

  public getLatestUpdateActivity(buddyId: number, userId: number): string {
    const activities = this.getSwappingActivitiesOfBuddy(buddyId, userId);
    if (activities.length > 0) {
      return this.helper.localizeDateString(activities[0].attributes.created_at);
    } else {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
